import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Trash2,
  Copy,
  Pause,
  Play,
  X,
  Network,
  ListOrdered,
  Globe,
  Save,
} from 'lucide-react';
import { useDebug, NetworkEntry, StepEntry } from '../../contexts/DebugContext';
import { useExtendedMode } from '../../contexts/ExtendedModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { auth as firebaseAuth } from '../../config/firebase';
import { useEnvBackground } from '../../hooks/useEnvBackground';
import { BG_COLOR_OPTIONS } from '../../utils/envBackground';

/**
 * DebugDrawer — panel lateral derecho para superadmins.
 *
 * - Default ABIERTO al primer mount.
 * - Pestaña vertical pegada al borde derecho cuando está cerrado.
 * - Cero overhead para no-superadmins (este componente no se monta).
 * - El log queda scoped a la sesión de página: se limpia automáticamente
 *   en beforeunload (F5, cierre tab) y popstate (back/forward, pushState
 *   manual). Ver DebugContext.tsx para los listeners.
 */

type Section = 'network' | 'steps' | 'page' | 'perf';

const fmtClock = (ms: number) => {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const fmtSize = (bytes?: number) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
};

const durColor = (d?: number) => {
  if (d == null) return 'text-gray-400';
  if (d < 500) return 'text-green-400';
  if (d < 1500) return 'text-yellow-400';
  return 'text-red-400';
};

const statusColor = (s?: number) => {
  if (s == null) return 'text-gray-400';
  if (s >= 500) return 'text-red-400';
  if (s >= 400) return 'text-orange-400';
  if (s >= 300) return 'text-yellow-400';
  return 'text-green-400';
};

const safeJson = (val: any, max = 2048): string => {
  try {
    if (val == null) return '';
    if (typeof val === 'string') return val.length > max ? val.slice(0, max) + '… (truncated)' : val;
    const s = JSON.stringify(val, null, 2);
    return s.length > max ? s.slice(0, max) + '\n… (truncated)' : s;
  } catch {
    return '[unserializable]';
  }
};

const NetworkRow: React.FC<{
  entry: NetworkEntry;
  selected: boolean;
  onSelect: (id: string) => void;
}> = ({ entry, selected, onSelect }) => {
  return (
    <div
      className={`border-b border-gray-800 ${selected ? 'bg-gray-800/70' : ''}`}
    >
      <button
        type="button"
        onClick={() => onSelect(entry.id)}
        className={`w-full text-left px-2 py-1 hover:bg-gray-800 text-xs font-mono ${
          selected ? 'bg-gray-800/70' : ''
        }`}
      >
        {/* Línea 1: method + status + timing + size */}
        <div className="flex items-center gap-2">
          <span className="text-blue-400 w-12 flex-shrink-0">{entry.method}</span>
          <span className={`w-10 flex-shrink-0 ${statusColor(entry.status)}`}>
            {entry.status ?? (entry.error ? 'ERR' : '...')}
          </span>
          <span className={`flex-1 tabular-nums ${durColor(entry.durationMs)}`}>
            {entry.durationMs != null ? `${entry.durationMs}ms` : '—'}
          </span>
          <span className="tabular-nums text-gray-400">{fmtSize(entry.sizeBytes)}</span>
        </div>
        {/* Línea 2: URL completa (alineada a la izquierda con method) */}
        <div className="text-gray-200 break-all leading-tight pt-0.5">
          {entry.url}
        </div>
      </button>
    </div>
  );
};

// Detalles del request seleccionado — mostrados en el bottom dock.
const NetworkDetails: React.FC<{ entry: NetworkEntry }> = ({ entry }) => {
  // Truncar responseData a sample (1-2 items si es array, 800 chars si es texto/objeto).
  // Útil para compartir el debug sin payloads de MB.
  const sampleResponseData = (() => {
    const rd = entry.responseData;
    if (rd == null) return null;
    if (Array.isArray(rd)) return { __sample_of: rd.length, items: rd.slice(0, 2) };
    if (typeof rd === 'object') {
      // Buscar arrays dentro del objeto (típico: { data: [...], total: N })
      const result: any = {};
      for (const k of Object.keys(rd)) {
        const v = (rd as any)[k];
        if (Array.isArray(v)) {
          result[k] = { __sample_of: v.length, items: v.slice(0, 2) };
        } else if (typeof v === 'object' && v != null) {
          result[k] = '[object — full en JSON]';
        } else {
          result[k] = v;
        }
      }
      return result;
    }
    const s = String(rd);
    return s.length > 800 ? s.slice(0, 800) + `... (truncated, ${s.length} chars total)` : s;
  })();

  // DEBUG payload — metadata + sample del response. Pensado para pegar en chat
  // y debuggear sin sobrecargar (cap ~2-5KB típico).
  const debugPayload = {
    method: entry.method,
    url: entry.fullUrl,
    status: entry.status ?? null,
    statusText: entry.statusText ?? null,
    durationMs: entry.durationMs ?? null,
    sizeBytes: entry.sizeBytes ?? null,
    error: entry.error ?? null,
    requestHeaders: entry.requestHeaders,
    requestData: entry.requestData,
    responseHeaders: entry.responseHeaders,
    responseDataSample: sampleResponseData,
    startedAt: (entry as any).startedAt,
    endedAt: (entry as any).endedAt,
  };

  // JSON payload — request + response completo (sin truncar). Para análisis local.
  const fullPayload = {
    method: entry.method,
    url: entry.fullUrl,
    status: entry.status ?? null,
    statusText: entry.statusText ?? null,
    durationMs: entry.durationMs ?? null,
    sizeBytes: entry.sizeBytes ?? null,
    error: entry.error ?? null,
    requestHeaders: entry.requestHeaders,
    requestData: entry.requestData,
    responseHeaders: entry.responseHeaders,
    responseData: entry.responseData,
    startedAt: (entry as any).startedAt,
    endedAt: (entry as any).endedAt,
  };
  return (
  <div className="px-3 py-2 text-[11px] font-mono space-y-2">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-blue-400 font-semibold">{entry.method}</span>
      <button
        type="button"
        onClick={() => { try { navigator.clipboard.writeText(JSON.stringify(debugPayload, null, 2)); } catch { /* */ } }}
        className="ml-auto px-2 py-0.5 text-[10px] bg-gray-800 hover:bg-gray-700 rounded text-gray-200"
        title="Copiar request + headers + status + sample del response (compacto para compartir)"
      >
        <Copy className="w-3 h-3 inline-block mr-1" />DEBUG
      </button>
      <button
        type="button"
        onClick={() => { try { navigator.clipboard.writeText(JSON.stringify(fullPayload, null, 2)); } catch { /* */ } }}
        className="px-2 py-0.5 text-[10px] bg-gray-800 hover:bg-gray-700 rounded text-gray-200"
        title="Copiar request + response completo (puede ser grande)"
      >
        <Copy className="w-3 h-3 inline-block mr-1" />JSON
      </button>
    </div>
    {/* Bloque info: response code + duration + size labelados, para que se vean claros y queden bien en el JSON */}
    <div className="grid grid-cols-3 gap-2 bg-gray-800/40 rounded px-2 py-1.5">
      <div>
        <div className="text-gray-400 uppercase text-[9px] tracking-wide">Status</div>
        <div className={`text-[12px] font-semibold ${statusColor(entry.status)}`}>
          {entry.status ?? (entry.error ? 'ERR' : '...')}
          {entry.statusText ? <span className="text-gray-400 font-normal text-[10px] ml-1">{entry.statusText}</span> : null}
        </div>
      </div>
      <div>
        <div className="text-gray-400 uppercase text-[9px] tracking-wide">Duration</div>
        <div className={`text-[12px] font-semibold tabular-nums ${durColor(entry.durationMs)}`}>
          {entry.durationMs != null ? `${entry.durationMs} ms` : '—'}
        </div>
      </div>
      <div>
        <div className="text-gray-400 uppercase text-[9px] tracking-wide">Size</div>
        <div className="text-[12px] font-semibold tabular-nums text-gray-200">{fmtSize(entry.sizeBytes)}</div>
      </div>
    </div>
    <div>
      <div className="text-gray-500 uppercase tracking-wide mb-0.5">Full URL</div>
      <div className="text-gray-300 break-all">{entry.fullUrl}</div>
    </div>
    {entry.requestHeaders && (
      <div>
        <div className="text-gray-500 uppercase tracking-wide mb-0.5">Request headers</div>
        <pre className="text-gray-300 whitespace-pre-wrap break-all">
          {safeJson(entry.requestHeaders, 1024)}
        </pre>
      </div>
    )}
    {entry.requestData != null && (
      <div>
        <div className="text-gray-500 uppercase tracking-wide mb-0.5">Request body</div>
        <pre className="text-gray-300 whitespace-pre-wrap break-all">
          {safeJson(entry.requestData)}
        </pre>
      </div>
    )}
    {entry.responseHeaders && (
      <div>
        <div className="text-gray-500 uppercase tracking-wide mb-0.5">Response headers</div>
        <pre className="text-gray-300 whitespace-pre-wrap break-all">
          {safeJson(entry.responseHeaders, 1024)}
        </pre>
      </div>
    )}
    {entry.responseData != null && (
      <div>
        <div className="text-gray-500 uppercase tracking-wide mb-0.5">Response body</div>
        <pre className="text-gray-300 whitespace-pre-wrap break-all">
          {safeJson(entry.responseData)}
        </pre>
      </div>
    )}
    {entry.error && (
      <div>
        <div className="text-red-400 uppercase tracking-wide mb-0.5">Error</div>
        <pre className="text-red-300 whitespace-pre-wrap break-all">{entry.error}</pre>
      </div>
    )}
  </div>
  );
};

const StepRow: React.FC<{ entry: StepEntry; baseTime: number }> = ({ entry, baseTime }) => {
  const phaseColor =
    entry.phase === 'start'
      ? 'text-blue-400'
      : entry.phase === 'end'
      ? 'text-green-400'
      : 'text-gray-400';
  const offset = entry.at - baseTime;
  return (
    <div className="px-2 py-1 border-b border-gray-800 text-[11px] font-mono flex items-start gap-2">
      <span className="w-12 flex-shrink-0 text-gray-500 tabular-nums">
        +{(offset / 1000).toFixed(1)}s
      </span>
      <span className={`w-10 flex-shrink-0 uppercase ${phaseColor}`}>{entry.phase}</span>
      <span className="flex-1 text-gray-200 break-all">{entry.label}</span>
      {entry.durationMs != null && (
        <span className={`tabular-nums ${durColor(entry.durationMs)}`}>
          {entry.durationMs}ms
        </span>
      )}
    </div>
  );
};

const PerfSection: React.FC = () => {
  const [nav, setNav] = useState<PerformanceNavigationTiming | null>(null);
  useEffect(() => {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries.length) setNav(entries[0] as PerformanceNavigationTiming);
    } catch {
      // ignore
    }
  }, []);
  if (!nav) {
    return <div className="text-xs text-gray-500 p-2">Sin datos de navigation timing.</div>;
  }
  const rows: [string, number][] = [
    ['DNS', nav.domainLookupEnd - nav.domainLookupStart],
    ['TCP', nav.connectEnd - nav.connectStart],
    ['TTFB', nav.responseStart - nav.requestStart],
    ['Response', nav.responseEnd - nav.responseStart],
    ['DOM ready', nav.domContentLoadedEventEnd],
    ['Load', nav.loadEventEnd],
  ];
  return (
    <div className="p-2 space-y-1">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between text-xs font-mono">
          <span className="text-gray-400">{k}</span>
          <span className="tabular-nums text-gray-200">{Math.round(v)}ms</span>
        </div>
      ))}
    </div>
  );
};

// ===== Snapshot endpoint (botón Save manual) =====
const isDevelopment = process.env.NODE_ENV === 'development';
const SNAPSHOT_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL
  : (isDevelopment ? 'http://localhost:3001' : '');
const SNAPSHOT_ENDPOINT = `${SNAPSHOT_BASE_URL}/api/admin/debug-snapshot`;

const DebugDrawer: React.FC = () => {
  const debug = useDebug();
  const { extendedMode, setExtendedMode, available: extendedModeAvailable } = useExtendedMode();
  const { currentUser } = useAuth();
  const { selectedClient } = useClient();
  const {
    env: currentEnv,
    activeColorId,
    defaultColorId,
    hasOverride,
    setColor: setEnvBgColor,
    resetToDefault: resetEnvBg,
  } = useEnvBackground();
  const [open, setOpen] = useState(false); // Default CERRADO — superadmin lo abre on-demand desde el botón flotante
  const [section, setSection] = useState<Section>('network');
  const [now, setNow] = useState(Date.now());
  const [autoScroll, setAutoScroll] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ path: string; sizeBytes: number } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const networkScrollRef = useRef<HTMLDivElement | null>(null);
  const stepsScrollRef = useRef<HTMLDivElement | null>(null);

  // Bottom dock para detalles del request seleccionado.
  const [selectedNetworkId, setSelectedNetworkId] = useState<string | null>(null);
  const [dockHeight, setDockHeight] = useState(260); // px
  const dockResizingRef = useRef(false);

  // Feedback visual "Copy All" → cambia a "Copied" por 2s.
  const [copyAllStatus, setCopyAllStatus] = useState<'idle' | 'copied'>('idle');
  // Feedback visual "Save Perf" → 'saving' | 'saved' | 'error'
  const [savePerfStatus, setSavePerfStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [savePerfPath, setSavePerfPath] = useState<string | null>(null);

  // Auto-scroll "smart": si el user scrollea arriba manualmente, se pausa
  // hasta que vuelva al fondo (patrón Chrome DevTools). El checkbox del
  // header es override manual ("forzar off").
  const userPausedScrollRef = useRef<{ network: boolean; steps: boolean }>({
    network: false,
    steps: false,
  });

  // Reloj acumulado (tick cada segundo)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll al fondo cuando llegan nuevas entradas — solo si:
  //  (a) el checkbox "auto-scroll" está ON (override manual), Y
  //  (b) el usuario no se desplazó manualmente hacia arriba (smart pause).
  useEffect(() => {
    if (!autoScroll) return;
    if (section === 'network' && networkScrollRef.current && !userPausedScrollRef.current.network) {
      networkScrollRef.current.scrollTop = networkScrollRef.current.scrollHeight;
    }
    if (section === 'steps' && stepsScrollRef.current && !userPausedScrollRef.current.steps) {
      stepsScrollRef.current.scrollTop = stepsScrollRef.current.scrollHeight;
    }
  }, [debug.network.length, debug.steps.length, section, autoScroll]);

  // Detectar scroll manual: si el user no está "cerca del fondo" (>40px gap),
  // marcar como pausado. Cuando vuelve al fondo, reanudar.
  const handleListScroll = useCallback(
    (which: 'network' | 'steps') => (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userPausedScrollRef.current[which] = distanceFromBottom > 40;
    },
    []
  );

  // Resize del dock inferior (drag desde el handle del top edge).
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dockResizingRef.current) return;
      // Altura medida desde el bottom de la ventana hasta el cursor.
      const next = Math.max(120, Math.min(window.innerHeight - 120, window.innerHeight - e.clientY));
      setDockHeight(next);
    };
    const onUp = () => {
      if (dockResizingRef.current) {
        dockResizingRef.current = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Si el item seleccionado se pierde (clear() o reset), limpiar selección.
  const selectedEntry = useMemo(
    () => (selectedNetworkId ? debug.network.find((e) => e.id === selectedNetworkId) || null : null),
    [selectedNetworkId, debug.network]
  );
  useEffect(() => {
    if (selectedNetworkId && !selectedEntry) setSelectedNetworkId(null);
  }, [selectedNetworkId, selectedEntry]);

  const elapsed = now - debug.startedAt;

  // `now` se usa intencionalmente como dependencia para que la URL del
  // pageInfo se refresque cada tick (el navigate sin router no genera
  // re-render automático aquí).
  const pageInfo = useMemo(
    () => ({
      url: typeof window !== 'undefined' ? window.location.href : '',
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      user: currentUser?.email || '—',
      client: selectedClient
        ? `${selectedClient.clientName} (${selectedClient.clientId})`
        : '— ninguno —',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser, selectedClient, now]
  );

  // Snapshot completo (network + steps + page + perf). Reusado por el botón
  // "Copiar" y por el botón "Save".
  const getSnapshot = useCallback(() => {
    let perf: any = null;
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length) {
        const nav = navEntries[0] as PerformanceNavigationTiming;
        perf = {
          dnsMs: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
          tcpMs: Math.round(nav.connectEnd - nav.connectStart),
          ttfbMs: Math.round(nav.responseStart - nav.requestStart),
          responseMs: Math.round(nav.responseEnd - nav.responseStart),
          domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
          loadMs: Math.round(nav.loadEventEnd),
        };
      }
    } catch {
      // ignore
    }
    return {
      copiedAt: new Date().toISOString(),
      elapsedMs: elapsed,
      page: pageInfo,
      network: debug.network,
      steps: debug.steps,
      perf,
    };
  }, [elapsed, pageInfo, debug.network, debug.steps]);

  const handleCopy = () => {
    try {
      // Snapshot completo: incluye los 4 tabs (network + steps + page + perf).
      navigator.clipboard.writeText(JSON.stringify(getSnapshot(), null, 2));
    } catch {
      // ignore
    }
  };

  // Payload compacto para pegar a una IA de perf — sin response bodies completos,
  // solo summary de 3-5 fields top-level. Cap ~200 chars en summary.
  const buildPerfAnalysisPayload = useCallback(() => {
    let perf: any = {};
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length) {
        const nav = navEntries[0] as PerformanceNavigationTiming;
        perf.domContentLoadedMs = Math.round(nav.domContentLoadedEventEnd);
        perf.loadEventMs = Math.round(nav.loadEventEnd);
      }
      const paints = performance.getEntriesByType('paint');
      const fcp = paints.find((p) => p.name === 'first-contentful-paint');
      if (fcp) perf.firstContentfulPaintMs = Math.round(fcp.startTime);
      const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
      if (lcpEntries.length) perf.largestContentfulPaintMs = Math.round(lcpEntries[lcpEntries.length - 1].startTime);
    } catch {
      // ignore
    }

    const summarize = (rd: any): string => {
      if (rd == null) return '';
      try {
        if (typeof rd === 'string') return rd.length > 200 ? rd.slice(0, 200) + '…' : rd;
        if (Array.isArray(rd)) return `array(${rd.length})`;
        if (typeof rd === 'object') {
          const out: any = {};
          for (const k of Object.keys(rd).slice(0, 5)) {
            const v = rd[k];
            if (Array.isArray(v)) out[k] = `array(${v.length})`;
            else if (typeof v === 'object' && v != null) out[k] = '[object]';
            else out[k] = v;
          }
          const s = JSON.stringify(out);
          return s.length > 200 ? s.slice(0, 200) + '…' : s;
        }
        return String(rd).slice(0, 200);
      } catch { return '[unserializable]'; }
    };

    return {
      _meta: {
        url: typeof window !== 'undefined' ? window.location.href : '',
        timestamp: new Date().toISOString(),
        totalRequests: debug.network.length,
        sessionDurationMs: elapsed,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        clientId: selectedClient?.clientId || null,
        userEmail: currentUser?.email || null,
      },
      _perf: perf,
      _steps: debug.steps.map((s) => `${s.phase}: ${s.label}`),
      requests: debug.network.map((e) => ({
        url: e.url,
        method: e.method,
        status: e.status ?? null,
        durationMs: e.durationMs ?? null,
        startTime: (e as any).startedAt ? (e as any).startedAt - debug.startedAt : null,
        size: e.sizeBytes ?? null,
        type: (e as any).type || 'fetch',
        responseSummary: summarize(e.responseData),
        error: e.error || undefined,
      })),
    };
  }, [debug.network, debug.steps, debug.startedAt, elapsed, selectedClient, currentUser]);

  const handleCopyAll = useCallback(() => {
    const payload = buildPerfAnalysisPayload();
    const json = JSON.stringify(payload, null, 2);
    const done = () => {
      setCopyAllStatus('copied');
      window.setTimeout(() => setCopyAllStatus('idle'), 2000);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(json).then(done).catch(() => {
          window.prompt('Copiá manualmente el JSON:', json);
        });
      } else {
        window.prompt('Copiá manualmente el JSON:', json);
      }
    } catch {
      window.prompt('Copiá manualmente el JSON:', json);
    }
  }, [buildPerfAnalysisPayload]);

  // Save perf dump al backend → escribe a C:/pulpou/debug/{context}_{ts}.json
  // y copia el path resultante al clipboard para pegarlo en chat con IA.
  const handleSavePerf = useCallback(async () => {
    if (savePerfStatus === 'saving') return;
    setSavePerfStatus('saving');
    setSavePerfPath(null);
    try {
      const user = firebaseAuth.currentUser;
      const token = user ? await user.getIdToken(false) : null;
      if (!token) {
        setSavePerfStatus('error');
        window.setTimeout(() => setSavePerfStatus('idle'), 2500);
        return;
      }
      const payload = buildPerfAnalysisPayload();
      // Contexto: cliente activo o "global"
      const context = (selectedClient as any)?.name?.toLowerCase().replace(/\s+/g, '-') ||
                      (selectedClient as any)?.clientId ||
                      'global';
      const res = await fetch('/api/debug/save-perf-dump', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payload, context }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.ok) {
        setSavePerfStatus('error');
        window.setTimeout(() => setSavePerfStatus('idle'), 2500);
        return;
      }
      setSavePerfPath(j.savedPath);
      // Copiar path al clipboard automáticamente
      try { await navigator.clipboard.writeText(j.savedPath); } catch { /* ignore */ }
      setSavePerfStatus('saved');
      window.setTimeout(() => setSavePerfStatus('idle'), 3500);
    } catch {
      setSavePerfStatus('error');
      window.setTimeout(() => setSavePerfStatus('idle'), 2500);
    }
  }, [savePerfStatus, buildPerfAnalysisPayload, selectedClient]);

  // Botón Save manual: POST snapshot al backend, copia path al clipboard,
  // muestra modal con resultado o error.
  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      let token: string | null = null;
      try {
        const user = firebaseAuth.currentUser;
        if (user) token = await user.getIdToken(false);
      } catch {
        token = null;
      }
      if (!token) {
        setSaveError('No se pudo obtener token de autenticación. ¿Estás logueado?');
        setSaving(false);
        return;
      }
      const snap = getSnapshot();
      const payload = JSON.stringify({ ...snap, __authToken: token });

      let res: Response | null = null;
      try {
        res = await fetch(SNAPSHOT_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });
      } catch (err: any) {
        setSaveError(`Error de red: ${err && err.message ? err.message : 'fetch failed'}`);
        setSaving(false);
        return;
      }

      let json: any = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }

      if (!res.ok) {
        const msg =
          (json && (json.error || json.message)) ||
          `HTTP ${res.status} ${res.statusText || ''}`.trim();
        setSaveError(`HTTP ${res.status}: ${msg}`);
        setSaving(false);
        return;
      }

      const savedPath: string = (json && json.path) || '';
      const savedSize: number = (json && json.sizeBytes) || 0;

      try {
        if (savedPath) await navigator.clipboard.writeText(savedPath);
      } catch {
        // si el clipboard falla, igual mostramos el modal con el path.
      }

      setSaveResult({ path: savedPath, sizeBytes: savedSize });
    } catch (err: any) {
      setSaveError(err && err.message ? err.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }, [saving, getSnapshot]);

  const copyPathAgain = useCallback(() => {
    if (!saveResult) return;
    try {
      navigator.clipboard.writeText(saveResult.path);
    } catch {
      // ignore
    }
  }, [saveResult]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed top-1/2 right-0 -translate-y-1/2 z-40 bg-gray-900 hover:bg-gray-800 text-gray-100 px-1.5 py-3 rounded-l-md border border-r-0 border-gray-700 shadow-lg flex flex-col items-center gap-1.5"
        title="Abrir Debug Drawer"
      >
        <Activity className="w-4 h-4 text-green-400" />
        <span
          className="text-[10px] font-mono tracking-wide text-gray-400"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          DEBUG
        </span>
      </button>
    );
  }

  return (
    <div
      className="fixed top-0 right-0 h-screen z-40 bg-gray-900 text-gray-100 border-l border-gray-700 shadow-2xl flex flex-col"
      style={{ width: 320 }}
    >
      {/* Header sticky */}
      <div className="flex-shrink-0 border-b border-gray-700 bg-gray-900">
        <div className="flex items-center justify-between px-2 py-1.5">
          <div className="flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-green-400" />
            <span className="text-xs font-semibold tracking-wide">DEBUG</span>
            <span className="text-[10px] font-mono text-gray-400 tabular-nums ml-1">
              {fmtClock(elapsed)}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => debug.setPaused(!debug.paused)}
              className="p-1 hover:bg-gray-800 rounded"
              title={debug.paused ? 'Reanudar' : 'Pausar'}
            >
              {debug.paused ? (
                <Play className="w-3.5 h-3.5 text-yellow-400" />
              ) : (
                <Pause className="w-3.5 h-3.5 text-gray-400" />
              )}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="p-1 hover:bg-gray-800 rounded"
              title="Copiar log (todos los tabs)"
            >
              <Copy className="w-3.5 h-3.5 text-gray-400" />
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="p-1 hover:bg-gray-800 rounded disabled:opacity-50"
              title="Guardar snapshot (todos los tabs) y copiar path"
            >
              <Save className={`w-3.5 h-3.5 ${saving ? 'text-gray-500 animate-pulse' : 'text-emerald-400'}`} />
            </button>
            <button
              type="button"
              onClick={debug.clear}
              className="p-1 hover:bg-gray-800 rounded"
              title="Limpiar log"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-400" />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-gray-800 rounded"
              title="Cerrar"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-t border-gray-800 text-[11px] font-mono">
          {(
            [
              { key: 'network', label: 'Network', icon: Network, count: debug.network.length },
              { key: 'steps', label: 'Steps', icon: ListOrdered, count: debug.steps.length },
              { key: 'page', label: 'Page', icon: Globe, count: undefined },
              { key: 'perf', label: 'Perf', icon: Activity, count: undefined },
            ] as { key: Section; label: string; icon: any; count?: number }[]
          ).map((tab) => {
            const Icon = tab.icon;
            const active = section === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSection(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1 py-1.5 border-b-2 transition-colors ${
                  active
                    ? 'border-green-400 text-gray-100 bg-gray-800/60'
                    : 'border-transparent text-gray-400 hover:text-gray-200'
                }`}
              >
                <Icon className="w-3 h-3" />
                <span>{tab.label}</span>
                {tab.count != null && tab.count > 0 && (
                  <span className="text-[9px] text-gray-500">({tab.count})</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {section === 'page' && (
          <div className="p-3 space-y-3 text-xs font-mono overflow-y-auto">
            {/* EXTENDED MODE switch — solo superadmin. Habilita columna LUPA en Publicaciones. */}
            {extendedModeAvailable && (
              <div className="border border-gray-700 rounded-md p-2 bg-gray-800/40">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    <span className="text-gray-100 font-semibold text-[11px] tracking-wide">
                      EXTENDED MODE
                    </span>
                    <span className="text-[10px] text-gray-400 leading-tight mt-0.5 max-w-[200px]">
                      Habilita botón LUPA en cada row de Publicaciones para ver ficha completa del producto.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setExtendedMode(!extendedMode)}
                    title={extendedMode ? 'Desactivar EXTENDED MODE' : 'Activar EXTENDED MODE'}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
                      extendedMode ? 'bg-emerald-500' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        extendedMode ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                <div className="mt-1.5 text-[10px] text-gray-500">
                  Estado: <span className={extendedMode ? 'text-emerald-400' : 'text-gray-400'}>
                    {extendedMode ? 'ON' : 'OFF'}
                  </span>
                  <span className="ml-2 text-gray-600">· persiste cross-reload</span>
                </div>
              </div>
            )}

            {/* COLOR DE FONDO POR AMBIENTE — tinte full-width según localhost/staging/prod */}
            <div className="border border-gray-700 rounded-md p-2 bg-gray-800/40">
              <div className="flex flex-col">
                <span className="text-gray-100 font-semibold text-[11px] tracking-wide">
                  COLOR DE FONDO
                </span>
                <span className="text-[10px] text-gray-400 leading-tight mt-0.5">
                  Tinte del fondo full-width según ambiente. Ambiente detectado:{' '}
                  <span className="text-emerald-400 uppercase">{currentEnv}</span>
                </span>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1">
                {BG_COLOR_OPTIONS.map((opt) => {
                  const active = opt.id === activeColorId;
                  const isEnvDefault = opt.id === defaultColorId;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setEnvBgColor(opt.id)}
                      className={`flex items-center gap-2 px-2 py-1 rounded text-[11px] border transition-colors ${
                        active
                          ? 'border-emerald-500 bg-emerald-900/30 text-gray-100'
                          : 'border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <span
                        className="w-4 h-4 rounded border border-gray-500 flex-shrink-0"
                        style={{ backgroundColor: opt.swatch }}
                      />
                      <span className="flex-1 text-left">{opt.label}</span>
                      {isEnvDefault && (
                        <span className="text-[9px] text-gray-500">default</span>
                      )}
                      {active && <span className="text-emerald-400 text-[10px]">✓</span>}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={resetEnvBg}
                disabled={!hasOverride}
                className="mt-2 w-full px-2 py-1 rounded text-[10px] border border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-800"
                title="Borra el override manual y vuelve al color default del ambiente"
              >
                Restaurar default del ambiente
              </button>
              <div className="mt-1.5 text-[10px] text-gray-500 leading-tight">
                {hasOverride ? (
                  <span>Override manual activo · persiste (localStorage)</span>
                ) : (
                  <span>Usando default del ambiente · sin persistencia</span>
                )}
              </div>
            </div>

            <div>
              <div className="text-gray-500 uppercase text-[10px] tracking-wide mb-0.5">URL</div>
              <div className="text-gray-200 break-all">{pageInfo.url}</div>
            </div>
            <div>
              <div className="text-gray-500 uppercase text-[10px] tracking-wide mb-0.5">User</div>
              <div className="text-gray-200">{pageInfo.user}</div>
            </div>
            <div>
              <div className="text-gray-500 uppercase text-[10px] tracking-wide mb-0.5">
                Cliente activo
              </div>
              <div className="text-gray-200 break-all">{pageInfo.client}</div>
            </div>
          </div>
        )}

        {section === 'network' && (
          <>
            <div className="flex items-center justify-between px-2 py-1 border-b border-gray-800 text-[11px] font-mono text-gray-200">
              <div className="flex items-center gap-2">
                <span><span className="font-semibold">{debug.network.length}</span> requests</span>
                <button
                  type="button"
                  onClick={handleCopyAll}
                  disabled={debug.network.length === 0}
                  className={`px-1.5 py-0.5 rounded text-[10px] border ${
                    copyAllStatus === 'copied'
                      ? 'border-green-500 bg-green-900/30 text-green-300'
                      : 'border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-800'
                  }`}
                  title="Copia todos los requests al portapapeles para análisis"
                >
                  {copyAllStatus === 'copied' ? '✓ Copied' : '📋 Copy All'}
                </button>
                <button
                  type="button"
                  onClick={handleSavePerf}
                  disabled={debug.network.length === 0 || savePerfStatus === 'saving'}
                  className={`px-1.5 py-0.5 rounded text-[10px] border ${
                    savePerfStatus === 'saved'
                      ? 'border-green-500 bg-green-900/30 text-green-300'
                      : savePerfStatus === 'error'
                      ? 'border-red-500 bg-red-900/30 text-red-300'
                      : 'border-gray-700 bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-800'
                  }`}
                  title="Guarda el JSON a C:/pulpou/debug/ y copia el path al portapapeles"
                >
                  {savePerfStatus === 'saving' ? '⏳ Saving…'
                   : savePerfStatus === 'saved' ? '✓ Path copied'
                   : savePerfStatus === 'error' ? '✕ Error'
                   : '💾 Save'}
                </button>
                {savePerfPath && savePerfStatus === 'saved' && (
                  <span className="text-[10px] text-gray-400 truncate max-w-[200px]" title={savePerfPath}>
                    {savePerfPath}
                  </span>
                )}
              </div>
              <label className="flex items-center gap-1 cursor-pointer text-gray-300">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="w-3 h-3"
                />
                <span>auto-scroll</span>
              </label>
            </div>
            <div
              ref={networkScrollRef}
              onScroll={handleListScroll('network')}
              className="flex-1 overflow-y-auto"
            >
              {debug.network.length === 0 ? (
                <div className="text-xs text-gray-300 p-3">Sin requests todavía.</div>
              ) : (
                debug.network.map((e) => (
                  <NetworkRow
                    key={e.id}
                    entry={e}
                    selected={e.id === selectedNetworkId}
                    onSelect={(id) =>
                      setSelectedNetworkId((curr) => (curr === id ? null : id))
                    }
                  />
                ))
              )}
            </div>
          </>
        )}

        {section === 'steps' && (
          <>
            <div className="flex items-center justify-between px-2 py-1 border-b border-gray-800 text-[10px] font-mono text-gray-500">
              <span>{debug.steps.length} steps</span>
            </div>
            <div
              ref={stepsScrollRef}
              onScroll={handleListScroll('steps')}
              className="flex-1 overflow-y-auto"
            >
              {debug.steps.length === 0 ? (
                <div className="text-xs text-gray-500 p-3">
                  Sin steps todavía. Emití eventos:
                  <pre className="mt-1 text-[10px] bg-black/40 p-1.5 rounded break-all whitespace-pre-wrap">
{`window.dispatchEvent(new CustomEvent('debug:step', {
  detail: { label: 'foo', phase: 'start' }
}))`}
                  </pre>
                </div>
              ) : (
                debug.steps.map((e) => (
                  <StepRow key={e.id} entry={e} baseTime={debug.startedAt} />
                ))
              )}
            </div>
          </>
        )}

        {section === 'perf' && <PerfSection />}
      </div>

      {/* Bottom dock — detalles del request seleccionado.
          Fijo dentro del drawer: NO se mueve con el scroll de la lista. */}
      {selectedEntry && (
        <div
          className="flex-shrink-0 border-t border-gray-700 bg-gray-950 flex flex-col"
          style={{ height: dockHeight }}
        >
          {/* Resize handle (drag desde el top edge del dock) */}
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              dockResizingRef.current = true;
              document.body.style.userSelect = 'none';
              document.body.style.cursor = 'ns-resize';
            }}
            className="h-1.5 w-full cursor-ns-resize bg-gray-800 hover:bg-green-600 transition-colors flex-shrink-0"
            title="Arrastrar para redimensionar"
          />
          {/* Header del dock */}
          <div className="flex-shrink-0 flex items-center justify-between px-2 py-1 border-b border-gray-800 bg-gray-900">
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wide truncate">
              Request details
            </span>
            <button
              type="button"
              onClick={() => setSelectedNetworkId(null)}
              className="p-1 hover:bg-gray-800 rounded"
              title="Cerrar dock"
            >
              <X className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
          {/* Body del dock — scrollable independiente */}
          <div className="flex-1 overflow-y-auto bg-black/30">
            <NetworkDetails entry={selectedEntry} />
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-700 px-2 py-1 text-[10px] font-mono text-gray-500 flex items-center justify-between">
        <span>{debug.paused ? 'pausado' : 'activo'}</span>
        <span>superadmin</span>
      </div>

      {/* Modal de save — error */}
      {saveError && !saveResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-lg w-full mx-4 p-4 text-gray-900">
            <div className="text-base font-semibold text-red-700 mb-2">Error al guardar snapshot</div>
            <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap break-all">{saveError}</div>
            <div className="flex justify-end">
              <button
                onClick={() => setSaveError(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de save — éxito */}
      {saveResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 max-w-2xl w-full mx-4 p-4 text-gray-900">
            <div className="text-base font-semibold text-gray-900 mb-1">Snapshot guardado</div>
            <div className="text-sm text-gray-600 mb-2">Path copiado al clipboard:</div>
            <div className="font-mono text-xs bg-gray-50 border border-gray-200 rounded p-2 break-all select-all mb-2">
              {saveResult.path}
            </div>
            <div className="text-[11px] text-gray-500 tabular-nums mb-3">
              {(saveResult.sizeBytes / 1024).toFixed(1)} KB
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={copyPathAgain}
                className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded"
              >
                Copiar de nuevo
              </button>
              <button
                onClick={() => setSaveResult(null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugDrawer;
